'use client';
import Button from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { useMediaQuery } from 'react-responsive';
import { Markdown } from '@repo/ui/src/components/Markdown';
import type { LinkProps } from '@repo/ui/src/types/link';
import type { HeaderVariationProps } from '@repo/ui/src/types/headerVariation';

import HeaderVariety from '@primitives/HeaderVariety';

interface SingleColumnTextProps {
  description?: string;
  title?: string;
  subTitle?: string;
  titleAlign?: 'Left' | 'Right' | 'Center';
  link?: Array<LinkProps>;
  asButton?: boolean;
  headerVariation?: HeaderVariationProps;
  order?: number;
  _testID?: string;
}

const SingleColumnText = ({
  description,
  title,
  subTitle,
  titleAlign,
  link,
  asButton,
  headerVariation,
  order,
  _testID,
}: SingleColumnTextProps) => {
  const isMediumOrSmallerScreen = useMediaQuery({ query: '(max-width: 767px)' });
  const _link = link?.[0];

  return (
    <div
      className="flex justify-center mb-0 py-12 px-5 lg:px-0"
      style={{ zIndex: order }}
      data-test-id={`single_column_text_${_testID}`}
    >
      <div className="container max-w-[1200px]">
        {/* ALIGN TITLE LEFT */}
        {titleAlign === 'Left' ? (
          <div className="grid grid-cols-[1fr_2fr]">
            <div className="px-3 flex justify-center">
              {title && (
                <HeaderVariety
                  defaultVariation="h2"
                  headerVariation={headerVariation?.headerVariation}
                >
                  {title}
                </HeaderVariety>
              )}
            </div>
            <div>
              {subTitle ? (
                <div className="row">
                  <h4 className="pl-3">{subTitle}</h4>
                </div>
              ) : null}
              {description ? (
                <div className="row">
                  <div className="pl-3 m-auto">
                    <Markdown className="break-words max-w-paragraph">{description}</Markdown>
                  </div>
                </div>
              ) : null}
              {_link && (
                <div className="mb-3">
                  <div className={asButton ? 'pl-3 mr-md-auto' : 'pl-3'}>
                    {asButton ? (
                      <Button as={Anchor} {..._link} className="px-12">
                        {_link.displayName}
                      </Button>
                    ) : (
                      <Anchor {..._link} className="underline font-bold">
                        {_link.displayName}
                      </Anchor>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {/* ALIGN TITLE RIGHT */}
        {titleAlign === 'Right' ? (
          <div className="row">
            {isMediumOrSmallerScreen ? (
              <div className="text-right">
                {title && (
                  <HeaderVariety
                    defaultVariation="h2"
                    headerVariation={headerVariation?.headerVariation}
                  >
                    {title}
                  </HeaderVariety>
                )}
              </div>
            ) : null}
            <div className="col-12 col-md-9">
              {subTitle ? (
                <div className="row">
                  <h4 className="pr-3">{subTitle}</h4>
                </div>
              ) : null}
              {description ? (
                <div className="row">
                  <div className="text-right md:text-left col-12 col-md-9">
                    <Markdown className="break-words">{description}</Markdown>
                  </div>
                </div>
              ) : null}
              {_link && (
                <div
                  className={isMediumOrSmallerScreen ? 'flex justify-items-end mb-6' : 'col-9 mb-3'}
                >
                  <div className={`${isMediumOrSmallerScreen ? '' : 'row'}`}>
                    {asButton ? (
                      <Button as={Anchor} {..._link} className="px-12">
                        {_link.displayName}
                      </Button>
                    ) : (
                      <Anchor {..._link} className="underline font-bold">
                        {_link.displayName}
                      </Anchor>
                    )}
                  </div>
                </div>
              )}
            </div>
            {!isMediumOrSmallerScreen ? (
              <div className="col-12 col-md-3 text-right">
                {title && (
                  <HeaderVariety
                    defaultVariation="h2"
                    headerVariation={headerVariation?.headerVariation}
                  >
                    {title}
                  </HeaderVariety>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ALIGN TITLE CENTER */}
        {titleAlign === 'Center' || !titleAlign ? (
          <div className="px-2">
            <div className={'flex justify-center items-center text-center px-0'}>
              {title && (
                <HeaderVariety
                  defaultVariation="h2"
                  headerVariation={headerVariation?.headerVariation}
                  className="flex justify-center px-4 pb-8"
                >
                  {title}
                </HeaderVariety>
              )}
            </div>
            {subTitle ? (
              <div>
                <h4 className="tra-text-web-heading-4 flex justify-center text-center px-4 pb-6">
                  <div className="flex justify-content mx-auto">{subTitle}</div>
                </h4>
              </div>
            ) : null}
            {description ? (
              <div className="flex flex-col justify-center items-center text-center">
                <Markdown className="max-w-paragraph m-auto break-words">{description}</Markdown>
              </div>
            ) : null}
            {_link ? (
              <div>
                <div className="flex mb-3 justify-center">
                  <div className={`mx-auto ${isMediumOrSmallerScreen ? 'w-100' : ''}`}>
                    {asButton ? (
                      <Button as={Anchor} {..._link} className="px-12">
                        {_link.displayName}
                      </Button>
                    ) : (
                      <Anchor {..._link} className="underline font-bold">
                        {_link.displayName}
                      </Anchor>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SingleColumnText;
